import { format, set } from "date-fns";

import Interval from "../store/Interval";

// Geometry + snapping constants shared by the calendar (week) grid.
export const HOUR_HEIGHT = 48; // px per hour row
export const DAY_MINUTES = 24 * 60;
export const SNAP_MINUTES = 15;
export const MIN_DURATION_MINUTES = 15;
// The vertical scroll position the week grid opens at (07:00), so coaches land
// on working hours instead of midnight.
export const DEFAULT_SCROLL_HOUR = 7;

export const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/** Minutes since midnight for a time-of-day Date (date part is ignored). */
export const minutesOfTimeOfDay = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes();

/**
 * Start/end minutes for an interval, or null when either bound is missing.
 * Used to place interval blocks on the grid.
 */
export const intervalMinutes = (
  interval: Interval
): { start: number; end: number } | null => {
  if (!interval.startTime || !interval.endTime) return null;
  return {
    start: minutesOfTimeOfDay(interval.startTime),
    end: minutesOfTimeOfDay(interval.endTime),
  };
};

export const clampMinutes = (minutes: number): number =>
  Math.min(Math.max(minutes, 0), DAY_MINUTES);

/** Snap a minute value to the grid step, clamped to the day. */
export const snapMinutes = (
  minutes: number,
  step: number = SNAP_MINUTES
): number => clampMinutes(Math.round(minutes / step) * step);

export const minutesToOffset = (minutes: number): number =>
  (minutes / 60) * HOUR_HEIGHT;

export const offsetToMinutes = (offset: number): number =>
  (offset / HOUR_HEIGHT) * 60;

/**
 * Build a time-of-day Date from minutes since midnight. The date part is
 * "today" to match how intervals loaded from the server are parsed
 * (parseTimeOfDay uses the current date as its missing-date fallback), which
 * keeps same-day comparisons in the overlap validation consistent. Minutes are
 * capped at 23:59 so an end-of-day block never wraps to the next calendar day.
 */
export const timeOfDayFromMinutes = (minutes: number): Date => {
  const capped = Math.min(clampMinutes(minutes), DAY_MINUTES - 1);
  return set(new Date(), {
    hours: Math.floor(capped / 60),
    minutes: capped % 60,
    seconds: 0,
    milliseconds: 0,
  });
};

/** Minutes since midnight for the current local time (for the "now" line). */
export const minutesNow = (): number => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};

/** e.g. 540 -> "9:00am". */
export const formatMinutes = (minutes: number): string =>
  format(timeOfDayFromMinutes(minutes), "h:mmaaa");

/** e.g. "9:00am - 12:00pm" for a rendered interval. */
export const formatMinutesRange = (start: number, end: number): string =>
  `${formatMinutes(start)} - ${formatMinutes(end)}`;

export const formatHourLabel = (hour: number): string => {
  if (hour === 0) return "12am";
  if (hour === 12) return "12pm";
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
};
